import type { RouteRecordRaw } from 'vue-router';
import { ALL_PIPELINES_ROLES, CRM_ROLES } from '../constants/roles';
import dashboardIcon from '../assets/dashboard.svg';
import opportunityIcon from '../assets/opportunity.svg';
import activitiesIcon from '../assets/activities.svg';
import settingIcon from '../assets/setting.svg';
import accessIcon from '../assets/access.svg';

/**
 * Modeled on `frontend_web_v3/src/routes/laboratory.ts`. `meta.middlewares` is intentionally NOT set
 * here — the shell owns `@/router/middlewares` and attaches `[md.auth, md.accessControl]` itself when
 * it registers this route (CRM_ARCHITECTURE.md §7, wiring point #2). Only `meta.title` / `meta.img` /
 * `meta.allowedRoles` are filled in on the children, since the shell's `getNavItemMeta()` derives the
 * left sidebar from them. `meta.needsPipeline` is the CRM's own: `CrmMain.vue` shows "No pipeline yet"
 * on those pages to someone granted none.
 *
 * The children ARE the sidebar, in this order, matching the mockup: Dashboard, Opportunity, All
 * activities, CRM access, Configuration CRM, Advanced config. Every other name in the module — views,
 * component folders, store namespaces, style partials, locale keys — is derived from these six entries
 * (docs/CRM_SECTION_CONTRACT.md §6). This file is shared; adding a sidebar entry is the one change
 * that belongs here.
 *
 * `meta.img` carries icons from this repo's own `src/assets`, imported above so the bundler resolves
 * them. The shell's `SidebarItem.vue` passes an already-resolved `img` straight to `<img :src>` and
 * only `require()`s bare filenames from its own icon folder.
 */

const crmRoutes: RouteRecordRaw = {
    path: '/crm',
    name: 'crm',
    redirect: { name: 'crm-opportunities' },
    component: () => import('../views/CrmMain.vue'),
    children: [
        {
            path: 'dashboard',
            name: 'crm-dashboard',
            component: () => import('../views/Dashboard.vue'),
            meta: {
                title: 'crm.dashboard.navTitle',
                img: dashboardIcon,
                allowedRoles: CRM_ROLES,
                needsPipeline: true,
            },
        },
        {
            path: 'opportunities',
            name: 'crm-opportunities',
            component: () => import('../views/OpportunityBoard.vue'),
            meta: {
                title: 'crm.opportunity.navTitle',
                img: opportunityIcon,
                allowedRoles: CRM_ROLES,
                needsPipeline: true,
            },
        },
        {
            path: 'all-activities',
            name: 'crm-all-activities',
            component: () => import('../views/AllActivities.vue'),
            meta: {
                title: 'crm.allActivities.navTitle',
                img: activitiesIcon,
                allowedRoles: CRM_ROLES,
                needsPipeline: true,
            },
        },
        {
            path: 'access',
            name: 'crm-access',
            component: () => import('../views/CrmAccess.vue'),
            meta: {
                title: 'crm.crmAccess.navTitle',
                img: accessIcon,
                allowedRoles: ALL_PIPELINES_ROLES,
                everyNeededPermissions: ['crm.access.view'],
            },
        },
        {
            path: 'configuration',
            name: 'crm-configuration',
            component: () => import('../views/ConfigurationCrm.vue'),
            meta: {
                title: 'crm.configurationCrm.navTitle',
                img: settingIcon,
                allowedRoles: CRM_ROLES,
            },
        },
        {
            path: 'advanced-config',
            name: 'crm-advanced-config',
            component: () => import('../views/AdvancedConfig.vue'),
            meta: {
                title: 'crm.advancedConfig.navTitle',
                img: settingIcon,
                superAdmin: true,
            },
        },
        // The opportunity wizard / detail page. Not a sidebar entry.
        //
        // `hidden: true` keeps this parameterised route out of the left sidebar, matching
        // `routes/workorder.ts` / `routes/component.ts` in the shell. It is required, not cosmetic:
        // the shell derives sidebar items from EVERY route child, and `SidebarItem.vue` renders
        // `t(item.title)` unguarded except behind `v-if="!item.hidden"` — so a child without both
        // `hidden` and a resolvable `title` reaches vue-i18n as `t(undefined)` and throws
        // "SyntaxError: Invalid arguments" on entering /crm. `title` is still declared because the
        // router's `updateDocumentMeta()` reads it for the browser tab.
        {
            path: 'opportunities/:id',
            name: 'crm-opportunity-detail',
            component: () => import('../views/OpportunityDetail.vue'),
            meta: {
                title: 'crm.opportunityDetail.navTitle',
                hidden: true,
                allowedRoles: CRM_ROLES,
                needsPipeline: true,
            },
        },
    ],
};

export default crmRoutes;
